import React, {useEffect, useState, useCallback, useContext} from 'react';
import { Link, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import '../css/postDetail.css';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faUser, faComment } from "@fortawesome/free-regular-svg-icons";
import Zoom from "react-medium-image-zoom";
import PostComment from "../components/PostComment";
import { faShareAlt } from "@fortawesome/free-solid-svg-icons";
import { formatDate } from "../utils/dateUtils";
import api from "../api";
import { AuthContext } from "../context/AuthContext";
import DOMPurify from "dompurify";

function PostDetail() {
    const { id } = useParams();
    const [post, setPost] = useState(null);
    const [likes, setLikes] = useState(0);
    const [liked, setLiked] = useState(false);
    const [userId, setUserId] = useState(null);
    const [showComments, setShowComments] = useState(false);
    const { t } = useTranslation();
    const accessToken = localStorage.getItem('access_token');
    const { isAuthenticated, isAdmin, isBanned } = useContext(AuthContext);

    const loadPostAndLikes = useCallback(() => {
        api.get(`${process.env.REACT_APP_API_URL}/api/posts/${id}`)
            .then(response => {
                setPost(response.data);
                setLikes(response.data.likes);
            })
            .catch(error => console.error('Error fetching post or likes', error));
    }, [id]);

    useEffect(() => {
        loadPostAndLikes();
    }, [loadPostAndLikes, accessToken]);

    const handleShare = (postId) => {
        const shareUrl = `${window.location.origin}/posts/${postId}`;
        navigator.clipboard.writeText(shareUrl)
            .then(() => {
                Swal.fire({
                    text: 'Post link copied to clipboard!',
                    icon: 'success',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            })
            .catch(error => console.error('Error copying link', error));
    };

    const handleLike = () => {
        if (!accessToken) {
            Swal.fire({
                text: t('messages.notLoggedIn'),
                icon: 'error',
                showCancelButton: true,
                confirmButtonText: t('posts.ok'),
                cancelButtonText: t('posts.cancel'),
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#00ff00',
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button',
                    cancelButton: 'swal2-dark-button'
                }
            });
            return;
        }

        const endpoint = `${process.env.REACT_APP_API_URL}/api/post/${id}/like`;
        const method = liked ? 'delete' : 'post';

        api({
            method,
            url: endpoint,
            headers: { 'Authorization': `Bearer ${accessToken}` }
        })
            .then(() => {
                setLiked(!liked);
                setLikes(prevLikes => liked ? prevLikes - 1 : prevLikes + 1);
            })
            .catch(() => {
                Swal.fire({
                    text: t('posts.alreadyLiked'),
                    icon: 'error',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#ff0000',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('posts.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            });
    };

    const toggleComments = () => {
        setShowComments(!showComments);
    };

    const renderMedia = (media_url) => {
        if (!media_url) return null;

        // YouTube
        if (media_url.includes('youtube.com') || media_url.includes('youtu.be')) {
            let embedUrl = '';

            if (media_url.includes('/playlist?list=')) {
                const urlParams = new URLSearchParams(media_url.split('?')[1]);
                const playlistId = urlParams.get('list');
                if (playlistId) {
                    embedUrl = `https://www.youtube.com/embed/videoseries?list=${playlistId}`;
                }
            } else {
                let videoId = '';

                if (media_url.includes('/shorts/')) {
                    const path = new URL(media_url).pathname;
                    const parts = path.split('/');
                    const shortsIndex = parts.indexOf('shorts');
                    if (shortsIndex !== -1 && shortsIndex + 1 < parts.length) {
                        const videoIdWithQuery = parts[shortsIndex + 1];
                        videoId = videoIdWithQuery.split('?')[0];
                    }
                } else if (media_url.includes('youtu.be')) {
                    videoId = media_url.split('youtu.be/')[1].split('?')[0];
                } else {
                    const urlParams = new URLSearchParams(media_url.split('?')[1]);
                    videoId = urlParams.get('v');
                }

                if (videoId) {
                    embedUrl = `https://www.youtube.com/embed/${videoId}`;
                }
            }

            if (embedUrl) {
                return (
                    <iframe
                        width="560"
                        height="315"
                        src={embedUrl}
                        title={media_url.includes('/playlist?list=') ? "YouTube playlist" : "YouTube video"}
                        frameBorder="0"
                        allowFullScreen
                    ></iframe>
                );
            } else {
                return <p>Error while loading video</p>;
            }
        }

        // Twitch clip
        if (media_url.includes('twitch.tv') && media_url.includes('/clip/')) {
            const parentDomain = 'superpeople.live';
            const clipId = media_url.split('/clip/')[1].split('?')[0];
            const embedUrl = `https://clips.twitch.tv/embed?clip=${clipId}&parent=${parentDomain}&autoplay=false`;

            return (
                <iframe
                    src={embedUrl}
                    height="480"
                    width="720"
                    frameBorder="0"
                    scrolling="no"
                    allowFullScreen={true}
                    title={`Twitch clip ${clipId}`}
                ></iframe>
            );
        }
        // Twitch video
        else if (media_url.includes('twitch.tv/videos/')) {
            const parentDomain = 'superpeople.live';
            const videoId = media_url.split('videos/')[1].split('?')[0];
            const embedUrl = `https://player.twitch.tv/?video=${videoId}&parent=${parentDomain}&autoplay=false`;

            return (
                <iframe
                    src={embedUrl}
                    height="480"
                    width="720"
                    frameBorder="0"
                    scrolling="no"
                    allowFullScreen={true}
                    title={`Twitch video ${videoId}`}
                ></iframe>
            );
        }

        // Image
        if (media_url.match(/\.(jpeg|jpg|gif|png)$/i)) {
            return (
                <Zoom>
                    <img
                        src={media_url}
                        alt={t('userProfile.userMedia')}
                        style={{ cursor: 'zoom-in' }}
                    />
                </Zoom>
            );
        }

        // Video
        if (media_url.match(/\.(mp4|webm|ogg)$/i)) {
            return (
                <video controls width="600">
                    <source src={media_url} type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
            );
        }

        // Default
        return (
            <a href={media_url} target="_blank" rel="noopener noreferrer">
                {media_url}
            </a>
        );
    };

    if (!post) {
        return <div>{t('header.loading')}</div>;
    }

    return (
        <div className="posts-page post-detail-wrapper">
            <div className="posts-list">
                <div className="section-title">
                    <span className="title-text">{t('header.post')}</span>
                    <span className="arrow-icon">›</span>
                </div>
                <div className="post-card no-top-border">
                    <div className="post-content">
                        <div className="post-creator">
                            <FontAwesomeIcon icon={faUser} />
                            <Link to={`/user/${post.user_id}`} className="post-username">
                                {post.username}
                            </Link>
                        </div>
                        <div className="post-info"
                             dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(post.content)}}
                        ></div>
                        <div className="post-media">
                            {renderMedia(post.media_url)}
                        </div>
                        <div className="post-date">
                            <FontAwesomeIcon icon={faCalendarAlt} /> {formatDate(post.created_at, { format: 'LLL' })}
                        </div>
                    </div>
                    <div className="post-actions">
                        <span
                            className={`like-count ${liked ? 'liked' : ''}`}
                            onClick={handleLike}
                            style={{cursor: isAuthenticated ? 'pointer' : 'default'}}
                        >
                            ❤ {likes || 0}
                        </span>
                        <button className="share-button" onClick={() => handleShare(post.id)}>
                            <FontAwesomeIcon icon={faShareAlt} className="icon"/>
                            {t('posts.share')}
                        </button>
                        <button className="comments-button" onClick={toggleComments}>
                            <FontAwesomeIcon icon={faComment} className="icon"/>
                            {t('posts.comments')} ({post.comments || 0})
                        </button>
                    </div>
                    {showComments && (
                        <PostComment
                            postId={id}
                            isAuthenticated={isAuthenticated}
                            userId={userId}
                            isAdmin={isAdmin}
                            isBanned={isBanned}
                            token={accessToken}
                            onCommentCountUpdate={(newCount) => {
                                setPost(prevPost => ({ ...prevPost, comments: newCount }));
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

export default PostDetail;
